import axios from 'axios';
import camelcaseKeys from 'camelcase-keys';
import { clientUid, apiUrl, mochipayApiUrl } from 'config';
import get from 'lodash/get';
import localDataService from 'service/local-data-service';
import { fromApi } from 'utils/api-helper';

export const prettify = obj => {
  return camelcaseKeys(obj, { deep: true });
};

export class AccessTokenRevokeError extends Error {}

// export const gasStationRequest = axios.CancelToken.source();

const API_VERSION = 'v3';
const MOCHIPAY_API_VERSION = 'v1';

class ApiService {
  constructor() {
    this.http = this.createHttp;
    this.http.interceptors.request.use(value => this.requestInterceptor(value));
    this.http.interceptors.response.use(
      value => prettify(value),
      error => error
    );

    this.mochipayHttp = this.createMochipayHttp;
    this.mochipayHttp.interceptors.request.use(value => this.requestInterceptor(value));
    this.mochipayHttp.interceptors.response.use(
      value => prettify(value),
      error => this.refreshTokenInterceptor(error)
    );

    this.refreshTokenHttp = this.createHttp;
    this.refreshTokenHttp.interceptors.request.use(value => this.requestInterceptor(value));
    this.refreshTokenHttp.interceptors.response.use(
      value => prettify(value),
      error => error
    );
  }

  createHttp = axios.create({
    baseURL: apiUrl(),
    timeout: 30000,
    validateStatus: status => {
      return status >= 200 && status < 300;
    },
  });

  createMochipayHttp = axios.create({
    baseURL: mochipayApiUrl(),
    timeout: 30000,
    validateStatus: status => {
      return status >= 200 && status < 300;
    },
  });

  requestInterceptor = config => {
    if (this.accessToken()) {
      config.headers['Authorization'] = this.accessToken();
    }
    config.headers['Mochipay-Client-Id'] = clientUid();
    return config;
  };

  accessToken = () => {
    const accessToken = get(localDataService.getAuth(), 'accessToken', undefined);
    return accessToken ? `Bearer ${accessToken}` : '';
  };

  refreshTokenInterceptor = async error => {
    if (axios.isCancel(error)) {
      console.log('Request canceled', error.message);
      return Promise.reject(error);
    }
    console.log(error);
    const {
      config: originalRequest,
      response: { status },
    } = error;
    if (status === 401) {
      const [err, data] = await fromApi(
        this.refreshTokenHttp.post(`/oauth/token`, {
          refresh_token: get(localDataService.getAuth(), 'refreshToken', ''),
          grant_type: 'refresh_token',
        })
      );
      if (err) {
        if (err.response.status === 401) {
          localDataService.clearStorage();
          return new AccessTokenRevokeError();
        } else {
          return err;
        }
      } else {
        localDataService.saveAuth(data.data);
        this.mochipayHttp.defaults.headers.Authorization = `${this.accessToken()}`;
        originalRequest.headers.Authorization = `${this.accessToken()}`;
        return axios(originalRequest);
      }
    } else {
      console.log(Promise.reject(error));
      return Promise.reject(error);
    }
  };

  // 場站

  fetchPoiCollections = () => {
    return this.http.get(`${API_VERSION}/poi_collections`);
  };

  fetchPois = () => {
    return this.mochipayHttp.get(`${MOCHIPAY_API_VERSION}/pois?cooperation_states=in_cooperated,coming_soon`);
  };

  fetchGasStations = () => {
    return this.mochipayHttp.get(
      `${MOCHIPAY_API_VERSION}/pois?cooperation_states=in_cooperated,coming_soon&store_types=GasStation`
      // {
      //   cancelToken: gasStationRequest.token,
      // }
    );
  };

  fetchParkinglots = () => {
    return this.mochipayHttp.get(
      `${MOCHIPAY_API_VERSION}/pois?cooperation_states=in_cooperated,coming_soon&store_types=Parkinglot`
    );
  };

  fetchChainStores = page => {
    return this.http.get(`${API_VERSION}/chain_stores/list`, {
      params: {
        page,
        cooperation_states: 'in_cooperated',
      },
    });
  };

  // 優惠

  fetchPromotions = (transactionType, filter, distributor, isAps) => {
    return this.http.get(`${API_VERSION}/promotions`, {
      params: {
        transaction_type: transactionType,
        filter,
        description_with_type: false,
        distributor,
        parkinglot_online: isAps,
      },
    });
  };

  fetchPromotionCollections = distributor => {
    return this.http.get(`${API_VERSION}/promotion_collections`, {
      params: {
        distributor,
      },
    });
  };

  fetchPromotionUserUsageStatus = id => {
    return this.http.get(`${API_VERSION}/promotions/${id}/all_user_usage_status`);
  };

  fetchBoards = codes => {
    return this.http.get(`${API_VERSION}/bulletin_boards`, {
      params: { codes },
    });
  };

  fetchBootstraps = () => {
    return this.http.get(`${API_VERSION}/bootstraps`);
  };

  // 登入
  fetchOtp = (distributor, mobile) => {
    return this.mochipayHttp.post(`${MOCHIPAY_API_VERSION}/otp`, { distributor, mobile });
  };

  fetchAuth = (distributor, mobile, code) => {
    return this.mochipayHttp.post(`${MOCHIPAY_API_VERSION}/auth`, { distributor, mobile, code });
  };

  logout = () => {
    return this.mochipayHttp.post(`${MOCHIPAY_API_VERSION}/logout`);
  };

  // 會員
  fetchMe = () => {
    return this.mochipayHttp.get(`${MOCHIPAY_API_VERSION}/me`);
  };

  fetchLicensePlates = () => {
    return this.mochipayHttp.get(`${MOCHIPAY_API_VERSION}/license_plates`);
  };

  fetchActiveLicensePlates = () => {
    return this.mochipayHttp.get(`${MOCHIPAY_API_VERSION}/active_license_plates`);
  };

  ordersSummary = () => {
    return this.mochipayHttp.get(`${MOCHIPAY_API_VERSION}/orders_summary`);
  };

  fetchOrders = (transactionType, page) => {
    return this.mochipayHttp.get(`${MOCHIPAY_API_VERSION}/orders`, {
      params: {
        transaction_type: transactionType,
        page,
      },
    });
  };

  sendUserReports = (transactionType, plateNumber, startAt, endAt, ubn) => {
    return this.mochipayHttp.get(`${MOCHIPAY_API_VERSION}/user_reports`, {
      params: {
        transaction_types: transactionType,
        plate_number: plateNumber,
        start_at: startAt,
        end_at: endAt,
        ubn,
      },
    });
  };

  fetchOilPricing = () => {
    return axios.get('https://mochi-cloud-api.autopass.xyz/v1/gas_pricing');
  };
}

export default new ApiService();
